/**
 * Campos do formulário criar/editar usuário, reusados no Modal (desktop) e no
 * Sheet (mobile). A lógica vive em `useUsuarioForm`; aqui é só a apresentação.
 */
import { Icon } from "@caint/design-system";
import type { CargoUnidade, PapelKey } from "@caint/shared";
import { useEffect, useRef } from "react";
import { CARGOS_UNIDADE, CARGO_UNIDADE_ROTULO } from "../../lib/planoUnidade.ts";
import type { UsuarioFormApi } from "../../lib/useUsuarioForm.ts";
import { type CampoUsuario, resumoFaltas } from "../../lib/usuarioForm.ts";
import {
  EIXOS,
  GRUPO_ROTULO,
  type GrupoPapel,
  PAPEIS_ORDEM,
  PAPEL_GRUPO,
  PAPEL_ROTULO,
} from "../../lib/usuariosLista.ts";
import { UnidadeAutocomplete } from "../UnidadeAutocomplete.tsx";
import "../../styles/usuarios.css";

/**
 * O que cada perfil faz, em uma linha. Espelha as regras de `lib/navegacao.ts`,
 * `lib/ciclos.ts` (quem abre ciclo) e o guard da gestão de usuários — se uma
 * regra de acesso mudar lá, este texto muda junto.
 */
const PAPEL_AJUDA: Record<string, string> = {
  gqa: "Avaliador de campo: preenche o checklist da avaliação.",
  ggo: "Avaliador de campo: preenche o checklist da avaliação.",
  ncig: "Avaliador de campo: preenche o checklist da avaliação.",
  audin: "Supervisão (3ª linha): confere a avaliação e vê o Panorama da rede e a Auditoria.",
  coord: "Gestão: administra usuários e vê o Panorama da rede e a Auditoria.",
  presidente: "Gestão: abre e encerra ciclos de avaliação; vê o Panorama da rede e a Auditoria.",
  admin: "Acesso total: administra usuários e ciclos.",
  visualizador: "Acompanha o sistema em modo leitura — não altera nada.",
  unidade: "Conta do estabelecimento: acessa só o Portal da Unidade (Plano de Ação).",
};

const GRUPOS: readonly GrupoPapel[] = ["avaliacao", "supervisao", "gestao", "leitura", "unidade"];

export interface UsuarioCamposProps {
  f: UsuarioFormApi;
  /** Prefixo dos ids — desktop e mobile nunca coexistem, mas dois modais podem. */
  idPrefixo: string;
  /** A conta em edição é a do próprio gestor: ele não pode se desativar. */
  proprioUsuario?: boolean;
}

export function UsuarioCampos({ f, idPrefixo, proprioUsuario }: UsuarioCamposProps) {
  const raiz = useRef<HTMLDivElement>(null);
  // Salvar com algo faltando leva o teclado ao primeiro campo com erro — num
  // formulário que rola, o aviso do topo pode nem estar à vista. Depende do
  // CONTADOR de tentativas: a 2ª tentativa (outro campo errado) também move o foco.
  useEffect(() => {
    if (f.tentativas > 0) {
      raiz.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
    }
  }, [f.tentativas]);
  const id = (campo: string) => `${idPrefixo}-${campo}`;
  /** Atributos de erro do campo: borda vermelha + leitura pelo leitor de tela. */
  const erroDe = (campo: CampoUsuario) => {
    const msg = f.erroCampo(campo);
    return {
      msg,
      classe: msg ? " missing" : "",
      aria: msg ? { "aria-invalid": true, "aria-describedby": id(`${campo}-erro`) } : {},
    };
  };
  const eNome = erroDe("nome");
  const eEmail = erroDe("matricula");
  const eSenha = erroDe("senha");
  const eUnidade = erroDe("unidadeId");

  return (
    <div className="uf" ref={raiz}>
      {f.erro && (
        <p className="uf-alerta" role="alert">
          <Icon name="alert" size={16} />
          <span>{f.erro}</span>
        </p>
      )}
      {f.tentou && f.faltas.length > 0 && (
        <p className="u-faltas" role="alert">
          Para {f.ehEdicao ? "salvar" : "criar"}, falta: {resumoFaltas(f.faltas)}.
        </p>
      )}

      <fieldset className="uf-sec">
        <legend>Identificação</legend>
        <div className="m-field">
          <label htmlFor={id("nome")}>Nome</label>
          <input
            id={id("nome")}
            className={`m-input${eNome.classe}`}
            value={f.nome}
            onChange={(e) => f.setNome(e.target.value)}
            autoComplete="off"
            placeholder="Nome completo"
            {...eNome.aria}
          />
          {eNome.msg && (
            <p className="u-erro-campo" id={id("nome-erro")}>
              {eNome.msg}
            </p>
          )}
        </div>
        <div className="m-field">
          <label htmlFor={id("matricula")}>E-mail (login)</label>
          <input
            id={id("matricula")}
            type="email"
            className={`m-input${eEmail.classe}`}
            value={f.matricula}
            onChange={(e) => f.setMatricula(e.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="off"
            spellCheck={false}
            inputMode="email"
            placeholder="nome.sobrenome@emserh.ma.gov.br"
            {...eEmail.aria}
          />
          {eEmail.msg ? (
            <p className="u-erro-campo" id={id("matricula-erro")}>
              {eEmail.msg}
            </p>
          ) : (
            <p className="uf-dica">É com este e-mail que a pessoa entra no sistema.</p>
          )}
        </div>
        {f.emailMudou && (
          <p className="uf-aviso" aria-live="polite">
            <Icon name="alert" size={16} />
            <span>
              <b>O login passa a ser o novo e-mail.</b> Avise a pessoa — a senha continua a mesma.
            </span>
          </p>
        )}
        {!f.ehEdicao && (
          <div className="m-field">
            <label htmlFor={id("senha")}>Senha provisória (mín. 8 caracteres)</label>
            <div className="uf-linha">
              {/* Visível de propósito: o gestor precisa conferir o que vai enviar. */}
              <input
                id={id("senha")}
                className={`m-input uf-mono${eSenha.classe}`}
                value={f.senha}
                onChange={(e) => f.setSenha(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="off"
                spellCheck={false}
                {...eSenha.aria}
              />
              <button type="button" className="m-btn uf-gerar" onClick={f.gerarSenha}>
                <Icon name="refresh" size={15} />
                Gerar
              </button>
            </div>
            {eSenha.msg ? (
              <p className="u-erro-campo" id={id("senha-erro")}>
                {eSenha.msg}
              </p>
            ) : (
              <p className="uf-dica">Ao criar, você recebe a mensagem pronta para enviar à pessoa.</p>
            )}
          </div>
        )}
      </fieldset>

      <fieldset className="uf-sec">
        <legend>Acesso</legend>
        <div className="uf-grid">
          <div className="m-field">
            <label htmlFor={id("papel")}>Perfil</label>
            <select
              id={id("papel")}
              className="m-select"
              value={f.papel}
              onChange={(e) => f.setPapel(e.target.value as PapelKey)}
            >
              {GRUPOS.map((g) => (
                <optgroup key={g} label={GRUPO_ROTULO[g]}>
                  {PAPEIS_ORDEM.filter((p) => PAPEL_GRUPO[p] === g).map((p) => (
                    <option key={p} value={p}>
                      {PAPEL_ROTULO[p]}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {PAPEL_AJUDA[f.papel] && <p className="uf-dica">{PAPEL_AJUDA[f.papel]}</p>}
          </div>
          {f.ehUnidade ? (
            <div className="m-field">
              <label htmlFor={id("cargo")}>Cargo na unidade</label>
              <select
                id={id("cargo")}
                className="m-select"
                value={f.cargoUnidade}
                onChange={(e) => f.setCargoUnidade(e.target.value as CargoUnidade)}
              >
                {CARGOS_UNIDADE.map((c) => (
                  <option key={c} value={c}>
                    {CARGO_UNIDADE_ROTULO[c]}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="m-field">
              <label htmlFor={id("eixo")}>Eixo (opcional)</label>
              <select
                id={id("eixo")}
                className="m-select"
                value={f.eixo}
                onChange={(e) => f.setEixo(e.target.value)}
              >
                <option value="">Sem eixo</option>
                {EIXOS.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {f.ehUnidade && (
          <div className="m-field">
            <label htmlFor={id("unidade")}>Unidade vinculada</label>
            <UnidadeAutocomplete
              id={id("unidade")}
              valor={f.unidadeId}
              onChange={f.setUnidadeId}
              className={`m-input${eUnidade.classe}`}
              ariaErro={eUnidade.msg ? id("unidadeId-erro") : undefined}
            />
            {eUnidade.msg && (
              <p className="u-erro-campo" id={id("unidadeId-erro")}>
                {eUnidade.msg}
              </p>
            )}
          </div>
        )}
      </fieldset>

      {f.ehEdicao && (
        <label className={`uf-switch${proprioUsuario ? " travado" : ""}`}>
          <input
            type="checkbox"
            role="switch"
            aria-checked={f.ativo}
            checked={f.ativo}
            disabled={proprioUsuario}
            onChange={(e) => f.setAtivo(e.target.checked)}
          />
          <span className="uf-switch-trilho" aria-hidden="true" />
          <span className="uf-switch-texto">
            <b>Conta ativa</b>
            <small>
              {proprioUsuario
                ? "Esta é a sua conta — você não pode desativá-la."
                : "Desativada, a pessoa não consegue entrar. O histórico é preservado."}
            </small>
          </span>
        </label>
      )}
    </div>
  );
}
